import React, { useState } from 'react';
import { SparklesIcon, CalendarIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { postsService } from '../services/posts';
import { Link } from 'react-router-dom';

const JournalPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 9;

  const { data, isLoading } = useQuery({
    queryKey: ['posts', currentPage],
    queryFn: () => postsService.getPosts({ page: currentPage, per_page: perPage }),
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <img 
          src="/assets/images/bague-or-18-carats-diamants-aupiho-joaillerie.jpeg"
          alt="Bague en or 18 carats avec diamants - Aupiho Joaillerie"
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ position: 'fixed' }}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-light text-white mb-6 tracking-wide" style={{ fontFamily: 'MoonTime, cursive' }}>
              Journal
            </h1>
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-px bg-[#c69c70]"></div>
              <SparklesIcon className="h-8 w-8 text-[#c69c70]" />
              <div className="w-12 h-px bg-[#c69c70]"></div>
            </div>
            <p className="text-xl text-white/90 font-light max-w-2xl mx-auto">
              Découvrez nos actualités, conseils et inspirations autour de la joaillerie
            </p>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c69c70]"></div>
            </div>
          ) : data && data.posts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {data.posts.map((post) => {
                  const featuredImage = postsService.getFeaturedImage(post);
                  const excerpt = postsService.getExcerpt(post);

                  return (
                    <article 
                      key={post.id}
                      className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
                    >
                      {/* Image */}
                      <div className="relative h-64 overflow-hidden">
                        {featuredImage ? (
                          <img
                            src={featuredImage}
                            alt={post.title.rendered}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-[#497aab] to-[#5a8bc4] flex items-center justify-center">
                            <span className="text-white text-4xl font-light" style={{ fontFamily: 'MoonTime, cursive' }}>A</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                        
                        {/* Date Badge */}
                        <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
                          <CalendarIcon className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            {postsService.formatDate(post.date)}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-[#c69c70] transition-colors line-clamp-2">
                          {post.title.rendered}
                        </h2>
                        <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                          {excerpt}
                        </p>
                        <Link
                          to={`/blog/${post.slug}`}
                          className="inline-flex items-center gap-2 text-[#497aab] hover:text-[#c69c70] font-medium transition-colors group/link"
                        >
                          Lire l'article
                          <ArrowRightIcon className="h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Pagination */}
              {data.totalPages > 1 && (
                <div className="mt-12 flex justify-center gap-2">
                  {currentPage > 1 && (
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      className="px-4 py-2 border-2 border-[#497aab] text-[#497aab] hover:bg-[#497aab] hover:text-white transition-all duration-300 font-medium"
                    >
                      Précédent
                    </button>
                  )}
                  
                  {Array.from({ length: data.totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-4 py-2 font-medium transition-all duration-300 ${
                        page === currentPage
                          ? 'bg-[#c69c70] text-white'
                          : 'border-2 border-gray-300 text-gray-700 hover:border-[#c69c70] hover:text-[#c69c70]'
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  {currentPage < data.totalPages && (
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      className="px-4 py-2 border-2 border-[#497aab] text-[#497aab] hover:bg-[#497aab] hover:text-white transition-all duration-300 font-medium"
                    >
                      Suivant
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">Aucun article disponible pour le moment.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default JournalPage;

